import { APIAttachment, ArdoqId } from '@ardoq/api-types';
import { t } from '@ardoq/i18n';
import { RichTextEditorExtension } from '@ardoq/rich-text-editor';
import { popStackPageByName, pushStackPage } from '@ardoq/stack-page-manager';
import { action$, ofType } from '@ardoq/rxbeach';
import { showAppModule } from 'appContainer/actions';
import { createElement } from 'react';
import { take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DocumentArchiveFilePickerPage } from '../../documentArchive/filePicker/DocumentArchiveFilePickerPage';
import { i18nLocale$ } from '../../streams/i18nLocale$';

const DOCUMENT_ARCHIVE_PICKER_STACK_PAGE_PREFIX = 'documentArchiveFilePicker';

type DocumentArchivePickerOptions = {
  acceptButtonLabel: string;
  filter: 'image' | null;
  workspaceId?: ArdoqId;
};

const openDocumentArchivePickerAsync = (props: DocumentArchivePickerOptions) =>
  new Promise<false | APIAttachment[]>(resolve => {
    const stackPageName = `${DOCUMENT_ARCHIVE_PICKER_STACK_PAGE_PREFIX}-${uuidv4()}`;
    const navigationSubscription = action$
      .pipe(ofType(showAppModule), take(1))
      .subscribe(() => resolveAndClose(false));

    const resolveAndClose = (result: false | APIAttachment[]) => {
      navigationSubscription.unsubscribe();
      resolve(result);
      popStackPageByName(stackPageName);
    };

    pushStackPage(
      () =>
        createElement(DocumentArchiveFilePickerPage, {
          impureState: props,
          onCancel: () => resolveAndClose(false),
          onFinish: (selectedAttachments: APIAttachment[]) =>
            resolveAndClose(selectedAttachments),
        }),
      { stackPageName }
    );
  });

const openDocumentArchiveImagePickerAsync = (workspaceId?: ArdoqId) =>
  openDocumentArchivePickerAsync({
    // i18nLocale$.state OK here: snapshot-at-open-time; picker dialog body rendered once when the rich-text editor invokes the callback
    acceptButtonLabel: t(
      i18nLocale$.state,
      'documentArchive:filePicker.addSelectedImages',
      'Add selected images'
    ),
    filter: 'image',
    workspaceId,
  });

const openDocumentArchiveDocumentPickerAsync = (workspaceId?: ArdoqId) =>
  openDocumentArchivePickerAsync({
    // i18nLocale$.state OK here: snapshot-at-open-time; picker dialog body rendered once when the rich-text editor invokes the callback
    acceptButtonLabel: t(
      i18nLocale$.state,
      'documentArchive:filePicker.addSelectedDocuments',
      'Add selected documents'
    ),
    filter: null,
    workspaceId,
  });

const selectImage = async (workspaceId?: ArdoqId): Promise<APIAttachment[]> => {
  const selectedAttachments =
    await openDocumentArchiveImagePickerAsync(workspaceId);
  if (!selectedAttachments || !selectedAttachments.length) return [];
  return selectedAttachments;
};

const selectLink = async (workspaceId?: ArdoqId): Promise<APIAttachment[]> => {
  const selectedAttachments =
    await openDocumentArchiveDocumentPickerAsync(workspaceId);
  if (!selectedAttachments || !selectedAttachments.length) return [];
  return selectedAttachments;
};

export const documentArchiveAttachmentExtensions: RichTextEditorExtension[] = [
  {
    name: 'ImagePicker',
    imagePicker: selectImage,
  },
  {
    name: 'InternalLink',
    linkPicker: async (workspaceId?: ArdoqId) => {
      const links = await selectLink(workspaceId);
      return links.map(({ uri, filename }) => ({
        url: uri,
        label: filename,
      }));
    },
  },
];

export const richTextEditorConfig: RichTextEditorExtension[] = [
  'Heading',
  'Bold',
  'Italic',
  'Strike',
  'Blockquote',
  'HorizontalRule',
  'BulletList',
  'OrderedList',
  'Color',
  'Table',
  'Code',
  'CodeBlock',
  {
    name: 'Link',
  },
  ...documentArchiveAttachmentExtensions,
];

export const DESCRIPTION_EDITOR_MIN_HEIGHT = '230px';
